export const BROADCAST_VIEWS = ["2d", "3ddirector", "3doverview", "3dauto"] as const;
export type BroadcastView = (typeof BROADCAST_VIEWS)[number];
export const DEFAULT_BROADCAST_VIEWS: readonly BroadcastView[] = Object.freeze(["2d", "3ddirector", "3doverview"]);

export function parseBroadcastOptions(query: URLSearchParams): { views: readonly BroadcastView[]; dwellMs: number } {
  const views = (query.get("views") ?? "").split(",").map(value => value.trim())
    .filter((value): value is BroadcastView => (BROADCAST_VIEWS as readonly string[]).includes(value));
  const rawDwell = Number(query.get("dwell") ?? "45");
  const dwell = Number.isFinite(rawDwell) ? Math.min(180, Math.max(5, rawDwell)) : 45;
  return { views: views.length ? Object.freeze(views) : DEFAULT_BROADCAST_VIEWS, dwellMs: dwell * 1_000 };
}

export function broadcastViewUrl(view: BroadcastView, boot: string, battleSession?: string | null): string {
  const params = new URLSearchParams({ _b: boot, broadcast: "1" });
  if (battleSession !== undefined && battleSession !== null) params.set("battleSession", battleSession);
  if (view === "2d") return `/overlay.html?${params}`;
  params.set("cam", view === "3doverview" ? "overview" : view === "3dauto" ? "auto" : "director");
  return `/overlay3d.html?${params}`;
}

/** Phase policy is separate from asynchronous iframe transitions. */
export class BroadcastPolicy {
  private phase: string | null = null;
  private healthy = false;
  private rotationStartedAt: number;
  private retryAt = 0;

  constructor(readonly views: readonly BroadcastView[], readonly dwellMs: number, now: number) {
    if (!views.length || !Number.isFinite(dwellMs) || dwellMs <= 0) throw new Error("Invalid broadcast rotation");
    this.rotationStartedAt = now;
  }

  observe(phase: string, now: number): void {
    if (phase === "race" && (!this.healthy || this.phase !== "race")) this.rotationStartedAt = now;
    this.phase = phase;
    this.healthy = true;
  }

  disconnected(now: number): void {
    if (this.healthy) this.rotationStartedAt = now;
    this.healthy = false;
  }

  fail(now: number): void { this.retryAt = now + 30_000; }
  degraded(now: number): boolean { return now < this.retryAt; }
  rotating(now: number): boolean { return !this.degraded(now) && (!this.healthy || this.phase === "race"); }

  desired(now: number): BroadcastView {
    if (!this.rotating(now)) return "2d";
    const elapsed = Math.max(0, now - this.rotationStartedAt);
    return this.views[Math.floor(elapsed / this.dwellMs) % this.views.length];
  }
}

/** Latest-wins queue; returning to the in-flight target cancels a stale detour. */
export class BroadcastTransitionQueue {
  live: BroadcastView | null = null;
  target: BroadcastView | null = null;
  pending: BroadcastView | null = null;

  request(view: BroadcastView): BroadcastView | null {
    if (this.target !== null) {
      this.pending = view === this.target ? null : view;
      return null;
    }
    if (this.live === view) return null;
    this.target = view;
    return view;
  }

  settle(success: boolean): BroadcastView | null {
    if (success) this.live = this.target;
    this.target = null;
    const next = this.pending;
    this.pending = null;
    return next === null ? null : this.request(next);
  }

  reset(): void { this.live = null; this.target = null; this.pending = null; }
}

/** Report a persistently unhealthy live renderer once, allowing brief course reloads. */
export class BroadcastReadinessWatchdog {
  private unreadySince: number | null = null;
  private reported = false;
  constructor(readonly timeoutMs = 8_000) {}
  reset(): void { this.unreadySince = null; this.reported = false; }
  check(ready: boolean, now: number): boolean {
    if (ready) { this.reset(); return false; }
    this.unreadySince ??= now;
    if (this.reported || now - this.unreadySince < this.timeoutMs) return false;
    this.reported = true;
    return true;
  }
}
